/**
 * Utilitário para armazenar localizações pendentes no IndexedDB
 * Usado para sincronização quando o app está offline ou em background
 */

interface PendingLocation {
  id: string;
  osId: number;
  faseId: number;
  lat: number;
  lng: number;
  velocidade?: number;
  direcao?: number;
  precisao?: number;
  altitude?: number;
  timestamp: number;
  retryCount: number;
}

const DB_NAME = 'nexus-motorista-locations';
const STORE_NAME = 'pending-locations';
const DB_VERSION = 1;

class LocationStorage {
  private db: IDBDatabase | null = null;

  /**
   * Inicializar IndexedDB
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve();
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Erro ao abrir IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB inicializado');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: false
          });

          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('osId', 'osId', { unique: false });
          objectStore.createIndex('faseId', 'faseId', { unique: false });
        }
      };
    });
  }

  /**
   * Salvar localização pendente
   */
  async savePendingLocation(data: Omit<PendingLocation, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
    await this.init();

    const location: PendingLocation = {
      ...data,
      id: `${data.osId}-${data.faseId}-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('IndexedDB não inicializado'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(location);

      request.onsuccess = () => {
        console.log('💾 Localização salva no IndexedDB:', location.id);
        resolve(location.id);
      };

      request.onerror = () => {
        console.error('❌ Erro ao salvar localização:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Obter todas as localizações pendentes
   */
  async getPendingLocations(): Promise<PendingLocation[]> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve([]);
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('❌ Erro ao obter localizações pendentes:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Remover localização pendente
   */
  async removePendingLocation(id: string): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log('🗑️ Localização removida do IndexedDB:', id);
        resolve();
      };

      request.onerror = () => {
        console.error('❌ Erro ao remover localização:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Limpar todas as localizações pendentes
   */
  async clearPendingLocations(): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('🗑️ Todas as localizações pendentes foram removidas');
        resolve();
      };

      request.onerror = () => {
        console.error('❌ Erro ao limpar localizações:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Incrementar contador de tentativas
   */
  async incrementRetryCount(id: string): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const location = getRequest.result;
        if (location) {
          location.retryCount = (location.retryCount || 0) + 1;
          const putRequest = store.put(location);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }
}

export const locationStorage = new LocationStorage();
export type { PendingLocation };
